/**
 * MultiZoneDevicePicker — Unified zone picker with three sections:
 *   1. THIS DEVICE — local playback
 *   2. LOCAL AUDIO OUTPUTS — iOS route picker; macOS Tauri: cpal + CoreAudio IPC; Windows/Linux Tauri & browser: setSinkId
 *   3. NETWORK RENDERERS — discovered UPnP/RAAT/Bluesound devices
 *
 * Single Zone: picking ANY item stops all others; one destination plays.
 * Multi-Zone: checkboxes, enable when 2+ zones selected.
 */

import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useTranslation } from '../i18n/useTranslation';
import { isConnectorAvailable, getAudioOutputDevices, setAudioOutputDevice, getDeviceHardwareFormats, probeDeviceIp, listen as connectorListen } from '../lib/connector';
import { isIOSNativeApp, isDesktopMacOSUserAgent } from '../utils/platform';
import { useZoneProfileStore } from '../store/zoneProfileStore';
import { useHiddenDevicesStore } from '../store/hiddenDevicesStore';
import { useMaestroStore } from '../store/maestroStore';
import { getUsbDacDisplayName } from '../utils/usbDacNames';

function getZoneDisplayName(zone) {
  const profiles = useZoneProfileStore.getState().zones;
  const profile = profiles.find(p => p.zone_id === zone.id);
  return profile?.display_name || zone.name;
}

function getZoneSortOrder(zone) {
  const profiles = useZoneProfileStore.getState().zones;
  const profile = profiles.find(p => p.zone_id === zone.id);
  return profile?.sort_order ?? 999;
}

const GOLD = '#C9A84C';

const USB_CHIP_VENDORS = ['burr-brown', 'texas instruments', 'xmos', 'amanero', 'cmedia', 'c-media', 'savitech', 'via labs'];
function isUsbDacConnected(device) {
  if (!device.manufacturer) return false;
  const mfr = device.manufacturer.toLowerCase();
  return !USB_CHIP_VENDORS.some((v) => mfr.includes(v));
}

const CheckIcon = ({ color = GOLD, size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const SpeakerIcon = () => (
  <svg className="flex-shrink-0" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
    <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
  </svg>
);

const BluetoothIcon = () => (
  <svg className="flex-shrink-0" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="6.5 6.5 17.5 17.5 12 23 12 1 17.5 6.5 6.5 17.5" />
  </svg>
);

const UsbDacIcon = ({ color = 'currentColor' }) => (
  <svg className="flex-shrink-0" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="4" y="3" rx="2" width="16" height="14" />
    <path d="M9 21h6" />
    <path d="M12 17v4" />
    <circle cx="8.5" cy="10" r="1.5" fill={color} stroke="none" />
    <circle cx="15.5" cy="10" r="1.5" fill={color} stroke="none" />
    <path d="M8 7v0" strokeWidth="2.5" />
    <path d="M12 7v0" strokeWidth="2.5" />
    <path d="M16 7v0" strokeWidth="2.5" />
  </svg>
);

const KebabIcon = ({ color = 'var(--text-tertiary)' }) => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill={color} stroke="none">
    <circle cx="12" cy="5" r="2" />
    <circle cx="12" cy="12" r="2" />
    <circle cx="12" cy="19" r="2" />
  </svg>
);

function UsbDacActionMenu({ device, onRetest, onHide }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [retesting, setRetesting] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handleClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  const handleRetest = useCallback(async (e) => {
    e.stopPropagation();
    setRetesting(true);
    try {
      await onRetest(device.id);
    } finally {
      setRetesting(false);
      setOpen(false);
    }
  }, [device.id, onRetest]);

  const handleHide = useCallback((e) => {
    e.stopPropagation();
    onHide(device.id);
    setOpen(false);
  }, [device.id, onHide]);

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={(e) => { e.stopPropagation(); setOpen(!open); }}
        className="p-1 rounded hover:bg-white/10 transition-colors"
        title={t('player.usb_dac_actions') || 'Device actions'}
      >
        <KebabIcon />
      </button>
      {open && (
        <div
          className="absolute right-0 top-full mt-1 z-[120] rounded-lg shadow-lg py-1 min-w-[160px]"
          style={{ background: 'var(--bg-card)', border: '1px solid var(--border)' }}
        >
          <button
            onClick={handleRetest}
            disabled={retesting}
            className="w-full text-left px-3 py-1.5 text-xs font-medium transition-colors hover:bg-white/5"
            style={{ color: 'var(--text-primary)', opacity: retesting ? 0.5 : 1 }}
          >
            {retesting
              ? (t('player.usb_dac_retesting') || 'Testing...')
              : (t('player.usb_dac_retest') || 'Re-test capabilities')}
          </button>
          <button
            onClick={handleHide}
            className="w-full text-left px-3 py-1.5 text-xs font-medium transition-colors hover:bg-white/5"
            style={{ color: 'var(--text-secondary)' }}
          >
            {t('player.usb_dac_hide') || 'Hide from list'}
          </button>
        </div>
      )}
    </div>
  );
}

const SectionHeader = ({ children, dot }) => (
  <div className="px-2 py-1.5 text-[10px] font-bold uppercase tracking-wider flex items-center gap-1.5" style={{ color: 'var(--text-tertiary)' }}>
    {dot && <span style={{ color: dot, fontSize: 6 }}>●</span>}
    {children}
  </div>
);

const Divider = () => (
  <div style={{ height: 1, background: 'var(--border)', margin: '8px 8px' }} />
);

export default function MultiZoneDevicePicker({
  allZones,
  batonZones,
  uniqueNetworkZones,
  activeZone,
  zoneName,
  audioOutputs,
  isNativeiOS,
  multizoneEnabled,
  groupedZones,
  onSelectZone,
  onToggleZone,
  onEnableMultizone,
  onDisableMultizone,
  onClose,
}) {
  const { t } = useTranslation();
  const [mode, setMode] = useState(multizoneEnabled ? 'multi' : 'single');

  const handleModeSwitch = useCallback((newMode) => {
    setMode(newMode);
    if (newMode === 'single' && multizoneEnabled) {
      onDisableMultizone();
    }
  }, [multizoneEnabled, onDisableMultizone]);

  const selectedIds = useMemo(
    () => new Set(groupedZones.map((z) => z.zoneId)),
    [groupedZones]
  );

  // Platform detection
  const isiOS = useMemo(() => isIOSNativeApp(), []);
  const isTauri = useMemo(() => isConnectorAvailable(), []);
  const isBrowser = !isiOS && !isTauri;
  const isMacTauri = isTauri && isDesktopMacOSUserAgent();
  /** Windows/Linux Connector: CoreAudio IPC is macOS-only; route via Chromium setSinkId. */
  const tauriUsesWebAudioSink = isTauri && !isMacTauri;

  // Split batonZones: local device vs. others
  const localZone = useMemo(() => {
    return batonZones.find(z =>
      z.type === 'local' || z.type === 'browser' ||
      z.id === 'this_computer' || z.id === 'local_computer' || z.id === 'this_device'
    ) || batonZones[0];
  }, [batonZones]);

  const otherLocalZones = useMemo(() => {
    if (!localZone) return [];
    return batonZones.filter(z =>
      z.id !== localZone.id &&
      (z.type === 'local' || z.type === 'browser' ||
       z.id === 'this_computer' || z.id === 'local_computer' || z.id === 'this_device')
    );
  }, [batonZones, localZone]);

  const localNativeZones = useMemo(() => {
    return batonZones.filter(z => z.type === 'local-native');
  }, [batonZones]);

  // Tauri (all platforms): list outputs via Connector's cpal/CoreAudio enumeration.
  // macOS routes via CoreAudio IPC; Windows/Linux routes via Chromium setSinkId.
  const [connectorDevices, setConnectorDevices] = useState([]);
  const prevDeviceIdsRef = useRef(new Set());

  useEffect(() => {
    if (!isTauri || mode !== 'single') return;
    let cancelled = false;
    getAudioOutputDevices()
      .then((devices) => {
        if (!cancelled) {
          setConnectorDevices(devices);
          prevDeviceIdsRef.current = new Set(devices.map((d) => d.id));
        }
      })
      .catch(() => {});

    // Hot-plug: listen for device changes emitted by Connector.
    // When a device disappears from the OS, clear it from the selected state
    // so the "can't get rid of it" symptom is fixed at the root.
    let unlistenFn = null;
    connectorListen('audio-devices-changed', (devices) => {
      if (cancelled) return;
      const newIds = new Set(devices.map((d) => d.id));
      const prevIds = prevDeviceIdsRef.current;

      // Detect removals: devices in prevIds but not in newIds
      for (const oldId of prevIds) {
        if (!newIds.has(oldId)) {
          // Device was unplugged — if it was selected, deselect it
          if (useMaestroStore.getState().selectedUsbDac?.id === oldId) {
            useMaestroStore.getState().setSelectedUsbDac(null);
          }
        }
      }

      prevDeviceIdsRef.current = newIds;
      setConnectorDevices(devices);
    }).then((fn) => { unlistenFn = fn; }).catch(() => {});

    return () => {
      cancelled = true;
      if (unlistenFn) unlistenFn();
    };
  }, [isTauri, mode]);

  const hiddenIds = useHiddenDevicesStore((s) => s.hiddenIds);
  const hideDevice = useHiddenDevicesStore((s) => s.hideDevice);

  const usbDacs = useMemo(
    () => connectorDevices.filter((d) => {
      if (hiddenIds.includes(d.id)) return false;
      // macOS: classify by transport_type from CoreAudio
      if (d.transport_type === 'usb' && (d.is_likely_dac !== false)) return true;
      // Windows/Linux: cpal doesn't provide transport_type, so all non-default
      // devices with is_likely_dac are candidates. Exclude known built-in names.
      if (tauriUsesWebAudioSink && d.is_likely_dac) {
        const n = d.name.toLowerCase();
        const builtIn = ['speakers', 'realtek', 'built-in', 'headphones', 'hdmi',
          'displayport', 'digital audio', 'microsoft sound mapper'];
        return !builtIn.some((kw) => n.includes(kw));
      }
      return false;
    }),
    [connectorDevices, tauriUsesWebAudioSink, hiddenIds]
  );
  const otherConnectorDevices = useMemo(
    () => connectorDevices.filter((d) => !usbDacs.some((u) => u.id === d.id)),
    [connectorDevices, usbDacs]
  );

  // iOS current audio route
  const [iosRoute, setIosRoute] = useState(null);
  useEffect(() => {
    if (!isiOS) return;
    const handler = (e) => {
      setIosRoute(e.detail);
    };
    window.addEventListener('maestroAudioRoute', handler);
    window.addEventListener('maestroAudioRouteChanged', handler);
    window.__maestroNative?.getAudioRoute();
    return () => {
      window.removeEventListener('maestroAudioRoute', handler);
      window.removeEventListener('maestroAudioRouteChanged', handler);
    };
  }, [isiOS]);

  // --- Selection handlers that enforce mutual exclusion ---

  // Track which USB DAC the user explicitly selected (null = none / "This Device")
  // Initialize from global store so the picker reflects auto-selection on startup
  const [selectedUsbDacId, setSelectedUsbDacId] = useState(() => {
    return useMaestroStore.getState().selectedUsbDac?.id || null;
  });

  const isLocalActive = useMemo(() => {
    if (!localZone || !activeZone) return zoneName === localZone?.name;
    return activeZone.id === localZone.id;
  }, [activeZone, localZone, zoneName]);

  // On iOS, distinguish "This Device" (built-in speaker) from BT/AirPlay output.
  // Both are "local" zones, but only one should show the checkmark.
  const isBuiltInSpeaker = !isiOS || !iosRoute ||
    iosRoute.type === 'BuiltInSpeaker' || iosRoute.type === 'BuiltInReceiver';
  // Suppress "This Device" checkmark when a specific local output
  // (USB DAC or other device) is showing its own checkmark — avoid dual checkmarks.
  const hasConnectorDeviceWithCheckmark = isTauri &&
    (selectedUsbDacId || otherConnectorDevices.some((d) => d.is_default));
  const thisDeviceActive = isLocalActive && isBuiltInSpeaker && !selectedUsbDacId && !hasConnectorDeviceWithCheckmark;
  const btRouteActive = isLocalActive && !isBuiltInSpeaker;

  const handleSelectLocal = useCallback(() => {
    setSelectedUsbDacId(null);
    useMaestroStore.getState().setSelectedUsbDac(null);
    if (isMacTauri) {
      const builtIn = connectorDevices.find((d) => d.transport_type === 'built_in');
      if (builtIn && !builtIn.is_default) {
        setAudioOutputDevice(builtIn.id).catch(() => {});
        setConnectorDevices((prev) => prev.map((d) => ({ ...d, is_default: d.id === builtIn.id })));
      }
    } else if (tauriUsesWebAudioSink) {
      // Windows/Linux: reset to default browser output
      audioOutputs.selectDevice('default').catch(() => {});
    }
    if (localZone) onSelectZone(localZone);
  }, [localZone, onSelectZone, connectorDevices, isMacTauri, tauriUsesWebAudioSink, audioOutputs]);

  const handleSelectConnectorDevice = useCallback(async (deviceId, isUsbDac = false) => {
    if (isMacTauri) {
      // macOS: switch default output via CoreAudio IPC
      try {
        await setAudioOutputDevice(deviceId);
        setConnectorDevices((prev) => prev.map((d) => ({ ...d, is_default: d.id === deviceId })));
      } catch (err) {
        console.error('[ZonePicker] Failed to set device:', err);
        return;
      }
    } else if (tauriUsesWebAudioSink) {
      // Windows/Linux: match Connector device name to browser device, route via setSinkId
      const connDev = connectorDevices.find((d) => d.id === deviceId);
      const connName = (connDev?.name || deviceId).toLowerCase().trim();
      const browserDev = audioOutputs.devices.find((bd) => {
        const label = (bd.label || '').toLowerCase().trim();
        return label === connName || label.includes(connName) || connName.includes(label);
      });
      if (browserDev) {
        await audioOutputs.selectDevice(browserDev.deviceId);
      } else {
        // Trigger a permission request so browser can see device labels, then retry
        await audioOutputs.refreshDevices();
        const retryDev = audioOutputs.devices.find((bd) =>
          (bd.label || '').toLowerCase().trim().includes(connName)
        );
        if (retryDev) {
          await audioOutputs.selectDevice(retryDev.deviceId);
        } else {
          console.warn('[ZonePicker] No browser match for Connector device:', connDev?.name);
        }
      }
      setConnectorDevices((prev) => prev.map((d) => ({ ...d, is_default: d.id === deviceId })));
    }

    if (isUsbDac) {
      setSelectedUsbDacId(deviceId);
      const device = connectorDevices.find((d) => d.id === deviceId);
      const dacName = device ? getUsbDacDisplayName(device) : 'USB DAC';
      useMaestroStore.getState().setSelectedUsbDac({ id: deviceId, name: dacName, uid: device?.uid });
    } else {
      setSelectedUsbDacId(null);
      useMaestroStore.getState().setSelectedUsbDac(null);
    }

    if (!isLocalActive && localZone) {
      await new Promise((r) => setTimeout(r, 200));
      onSelectZone(localZone);
    } else {
      // Reload audio source to pick up the new output device
      const audios = document.querySelectorAll('audio');
      audios.forEach((audio) => {
        const wasPlaying = !audio.paused && audio.currentTime > 0;
        const savedTime = audio.currentTime;
        const src = audio.src || audio.currentSrc;
        if (wasPlaying && src) {
          audio.pause();
          audio.src = '';
          setTimeout(() => {
            audio.src = src;
            audio.currentTime = savedTime;
            audio.play().catch(() => {});
          }, 150);
        }
      });
    }
  }, [localZone, onSelectZone, isLocalActive, isMacTauri, tauriUsesWebAudioSink, connectorDevices, audioOutputs]);

  const handleSelectBrowserDevice = useCallback(async (deviceId) => {
    await audioOutputs.selectDevice(deviceId);
    if (localZone) onSelectZone(localZone);
  }, [audioOutputs, localZone, onSelectZone]);

  const handleShowIOSPicker = useCallback(() => {
    window.__maestroNative?.showAirPlayPicker();
  }, []);

  const handleSelectRenderer = useCallback((zone) => {
    setSelectedUsbDacId(null);
    useMaestroStore.getState().setSelectedUsbDac(null);
    if (mode === 'single') {
      onSelectZone(zone);
    } else {
      onToggleZone(zone);
    }
  }, [mode, onSelectZone, onToggleZone]);

  const handleRetestDevice = useCallback(async (deviceId) => {
    try {
      const result = await getDeviceHardwareFormats(deviceId);
      console.log('[ZonePicker] Re-test result for', deviceId, result);
      // Refresh device list to pick up any changes
      const devices = await getAudioOutputDevices();
      setConnectorDevices(devices);
    } catch (err) {
      console.error('[ZonePicker] Re-test failed:', err);
    }
  }, []);

  const handleHideDevice = useCallback((deviceId) => {
    hideDevice(deviceId);
    if (selectedUsbDacId === deviceId) {
      setSelectedUsbDacId(null);
      useMaestroStore.getState().setSelectedUsbDac(null);
    }
  }, [hideDevice, selectedUsbDacId]);

  // --- Render helpers ---

  const renderDeviceRow = (key, label, isActive, onClick, icon) => (
    <button
      key={key}
      onClick={onClick}
      className="text-left px-3 py-2 rounded-lg text-sm font-medium transition flex items-center gap-2"
      style={{
        background: isActive ? 'var(--bg-secondary)' : 'transparent',
        color: isActive ? GOLD : 'var(--text-primary)',
      }}
    >
      <span className="flex-shrink-0 flex items-center justify-center" style={{ width: 16, height: 16 }}>
        {isActive && <CheckIcon />}
      </span>
      {icon}
      <span className="truncate">{label}</span>
    </button>
  );

  const renderNetworkZoneItem = (zone) => {
    const displayName = getZoneDisplayName(zone);
    if (mode === 'single') {
      const isActive = activeZone && activeZone.id === zone.id;
      return renderDeviceRow(
        zone.id || zone.name,
        displayName,
        isActive,
        () => handleSelectRenderer(zone),
        null
      );
    }
    const isChecked = selectedIds.has(zone.id);
    return (
      <button
        key={zone.id || zone.name}
        onClick={() => handleSelectRenderer(zone)}
        className="text-left px-3 py-2 rounded-lg text-sm font-medium transition flex items-center gap-3"
        style={{
          background: isChecked ? 'rgba(201, 168, 76, 0.08)' : 'transparent',
          color: isChecked ? GOLD : 'var(--text-primary)',
        }}
      >
        <span
          className="flex-shrink-0 flex items-center justify-center rounded"
          style={{
            width: 18, height: 18,
            border: isChecked ? `2px solid ${GOLD}` : '2px solid var(--border)',
            background: isChecked ? GOLD : 'transparent',
            transition: 'all 0.15s ease',
          }}
        >
          {isChecked && (
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#1a1a2e" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          )}
        </span>
        <span className="truncate">{displayName}</span>
      </button>
    );
  };

  const hasUsbDacs = isTauri && usbDacs.length > 0;

  const hasOtherOutputs =
    isiOS ||
    (isMacTauri && otherConnectorDevices.length > 0) ||
    (tauriUsesWebAudioSink && (otherConnectorDevices.length > 0 || audioOutputs.isSupported)) ||
    (isBrowser && audioOutputs.isSupported && audioOutputs.devices.length > 0);

  return (
    <>
      <div className="fixed inset-0 z-[113]" onClick={onClose} />
      <div
        className="absolute right-4 z-[114] rounded-xl p-3 w-80 shadow-lg"
        style={{
          bottom: '100%',
          marginBottom: 8,
          background: 'var(--bg-card)',
          border: '1px solid var(--border)',
          maxHeight: '70vh',
          overflowY: 'auto',
        }}
      >
        {/* Mode toggle */}
        <div
          className="flex rounded-lg overflow-hidden mb-3"
          style={{ border: '1px solid var(--border)', background: 'var(--bg-secondary)' }}
        >
          <button
            onClick={() => handleModeSwitch('single')}
            className="flex-1 py-1.5 text-[11px] font-semibold uppercase tracking-wide transition"
            style={{
              background: mode === 'single' ? GOLD : 'transparent',
              color: mode === 'single' ? '#1a1a2e' : 'var(--text-secondary)',
            }}
          >
            {t('player.single_zone')}
          </button>
          <button
            onClick={() => handleModeSwitch('multi')}
            className="flex-1 py-1.5 text-[11px] font-semibold uppercase tracking-wide transition"
            style={{
              background: mode === 'multi' ? GOLD : 'transparent',
              color: mode === 'multi' ? '#1a1a2e' : 'var(--text-secondary)',
            }}
          >
            {t('player.multi_zone')}
          </button>
        </div>

        {/* Multi-zone status */}
        {mode === 'multi' && (
          <div
            className="px-3 py-2 mb-2 rounded-lg text-[11px] font-medium"
            style={{
              background: selectedIds.size >= 2 ? 'rgba(34, 197, 94, 0.08)' : 'rgba(201, 168, 76, 0.08)',
              color: selectedIds.size >= 2 ? '#22c55e' : GOLD,
            }}
          >
            {selectedIds.size >= 2
              ? t('player.zones_active', { count: selectedIds.size })
              : t('player.select_two_zones')}
          </div>
        )}

        {/* ── SECTION 0: USB DAC (elevated, top of picker) ── */}
        {mode === 'single' && hasUsbDacs && (
          <div className="mb-1">
            <SectionHeader dot={GOLD}>{t('player.usb_dac')}</SectionHeader>
            <div className="flex flex-col gap-0.5">
              {usbDacs.map((device) => {
                const isActive = device.id === selectedUsbDacId && isLocalActive;
                const dacDisplayName = getUsbDacDisplayName(device);
                return (
                  <div
                    key={device.id}
                    className="flex items-center gap-1 rounded-lg transition"
                    style={{
                      background: isActive ? 'rgba(201, 168, 76, 0.10)' : 'transparent',
                      border: isActive ? `1px solid rgba(201, 168, 76, 0.25)` : '1px solid transparent',
                    }}
                  >
                    <button
                      onClick={() => handleSelectConnectorDevice(device.id, true)}
                      className="flex-1 text-left px-3 py-2.5 text-sm font-medium transition flex items-center gap-2.5 min-w-0"
                      style={{ color: isActive ? GOLD : 'var(--text-primary)' }}
                    >
                      <span className="flex-shrink-0 flex items-center justify-center" style={{ width: 16, height: 16 }}>
                        {isActive ? <CheckIcon /> : null}
                      </span>
                      <UsbDacIcon color={isActive ? GOLD : 'var(--text-secondary)'} />
                      <div className="flex flex-col min-w-0">
                        <span className="truncate">{dacDisplayName}</span>
                        <span className="text-[10px] truncate" style={{ color: 'var(--text-tertiary)' }}>
                          {isUsbDacConnected(device) ? t('player.usb_dac_connected') : 'USB'}
                        </span>
                      </div>
                    </button>
                    <UsbDacActionMenu
                      device={device}
                      onRetest={handleRetestDevice}
                      onHide={handleHideDevice}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ── SECTION 1: THIS DEVICE ── */}
        {mode === 'single' && localZone && (
          <>
            {hasUsbDacs && <Divider />}
            <div className="mb-1">
              <SectionHeader>
                {t('player.this_device')}
                <span
                  title="System Audio plays through your computer's audio engine. For bit-perfect playback, use a network renderer."
                  style={{ marginLeft: 6, cursor: 'help', opacity: 0.5, fontSize: '0.7rem', verticalAlign: 'middle' }}
                >&#9432;</span>
              </SectionHeader>
              <div className="flex flex-col gap-0.5">
                {renderDeviceRow(
                  localZone.id || 'local',
                  localZone.name,
                  thisDeviceActive,
                  handleSelectLocal,
                  <SpeakerIcon />
                )}
                {otherLocalZones.map((zone) => {
                  const isActive = activeZone
                    ? activeZone.id === zone.id
                    : zoneName === zone.name;
                  return renderDeviceRow(
                    zone.id || zone.name,
                    zone.name,
                    isActive,
                    () => onSelectZone(zone),
                    <SpeakerIcon />
                  );
                })}
                {localNativeZones.map((zone) => {
                  const isActive = activeZone?.id === zone.id;
                  return renderDeviceRow(
                    zone.id,
                    zone.name,
                    isActive,
                    () => onSelectZone(zone),
                    <UsbDacIcon color={isActive ? GOLD : 'var(--text-secondary)'} />
                  );
                })}
              </div>
            </div>
          </>
        )}

        {/* Multi-zone: show all BATON zones as checkboxes */}
        {mode === 'multi' && (
          <div className="mb-1">
            <SectionHeader>{t('player.playback_zone')}</SectionHeader>
            <div className="flex flex-col gap-0.5">
              {batonZones.map((zone) => renderNetworkZoneItem(zone))}
            </div>
          </div>
        )}

        {/* ── SECTION 2: LOCAL AUDIO OUTPUTS (non-USB) ── */}
        {mode === 'single' && hasOtherOutputs && (
          <>
            <Divider />
            <div className="mb-1">
              <SectionHeader>{t('player.local_audio_outputs')}</SectionHeader>
              <div className="flex flex-col gap-0.5">

                {/* iOS: tap route name to switch to local; tap "Change..." to pick a different BT/AirPlay device */}
                {isiOS && (
                  <>
                    {renderDeviceRow(
                      'ios-bt-route',
                      iosRoute?.name || 'Speaker',
                      btRouteActive,
                      handleSelectLocal,
                      <BluetoothIcon />
                    )}
                    <button
                      onClick={handleShowIOSPicker}
                      className="text-left px-3 py-1.5 rounded-lg text-xs font-medium transition ml-6"
                      style={{ color: GOLD }}
                    >
                      {t('player.change_output')}
                    </button>
                  </>
                )}

                {/* Tauri (all platforms): non-USB outputs from Connector enumeration */}
                {isTauri && otherConnectorDevices.map((device) => (
                  renderDeviceRow(
                    device.id,
                    device.name,
                    device.is_default && isLocalActive,
                    () => handleSelectConnectorDevice(device.id),
                    device.transport_type === 'bluetooth' ? <BluetoothIcon /> : <SpeakerIcon />
                  )
                ))}

                {/* Browser (no Connector): Chromium setSinkId */}
                {isBrowser && audioOutputs.isSupported && audioOutputs.devices.map((device) => (
                  renderDeviceRow(
                    device.deviceId,
                    device.label,
                    device.deviceId === audioOutputs.activeDeviceId && isLocalActive,
                    () => handleSelectBrowserDevice(device.deviceId),
                    <BluetoothIcon />
                  )
                ))}
              </div>
            </div>
          </>
        )}

        {/* ── SECTION 3: NETWORK RENDERERS ── */}
        {uniqueNetworkZones.length > 0 && (
          <>
            <Divider />
            <div>
              <SectionHeader dot="#22c55e">{t('player.network_renderers')}</SectionHeader>
              <div className="flex flex-col gap-0.5">
                {[...uniqueNetworkZones]
                  .sort((a, b) => {
                    const oa = getZoneSortOrder(a);
                    const ob = getZoneSortOrder(b);
                    if (oa !== ob) return oa - ob;
                    return getZoneDisplayName(a).localeCompare(getZoneDisplayName(b));
                  })
                  .map((zone) => renderNetworkZoneItem(zone))}
              </div>
            </div>
          </>
        )}

        {/* ── Windows driver hint ── */}
        {tauriUsesWebAudioSink && mode === 'single' && usbDacs.length === 0 && (
          <>
            <Divider />
            <div className="px-3 py-2 text-[11px]" style={{ color: 'var(--text-tertiary)' }}>
              {t('player.usb_dac_win_hint')}
            </div>
          </>
        )}

        {/* ── SECTION 4: FIND MY DAC ── */}
        {isTauri && <FindMyDac />}
      </div>
    </>
  );
}

function FindMyDac() {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const [ip, setIp] = useState('');
  const [status, setStatus] = useState('');
  const [probing, setProbing] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (expanded && inputRef.current) inputRef.current.focus();
  }, [expanded]);

  const handleProbe = useCallback(async () => {
    const trimmed = ip.trim();
    if (!trimmed || probing) return;
    const ipPattern = /^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}$/;
    if (!ipPattern.test(trimmed)) {
      setStatus(t('player.find_dac_invalid_ip') || 'Please enter a valid IP address (e.g. 192.168.1.29)');
      return;
    }
    setProbing(true);
    setStatus(t('player.find_dac_searching') || 'Searching...');
    try {
      const name = await probeDeviceIp(trimmed);
      setStatus(`${t('player.find_dac_found') || 'Found'}: ${name}`);
      setIp('');
    } catch (e) {
      setStatus(e?.toString()?.replace(/^Error:\s*/, '') || t('player.find_dac_not_found') || 'No device found at this IP');
    } finally {
      setProbing(false);
    }
  }, [ip, probing, t]);

  if (!expanded) {
    return (
      <>
        <Divider />
        <button
          onClick={() => setExpanded(true)}
          className="w-full text-center py-2 text-xs transition-colors"
          style={{ color: 'var(--text-tertiary)' }}
          onMouseEnter={e => e.currentTarget.style.color = GOLD}
          onMouseLeave={e => e.currentTarget.style.color = 'var(--text-tertiary)'}
        >
          {t('player.find_my_dac') || "Can't find your DAC? Enter its IP address"}
        </button>
      </>
    );
  }

  return (
    <>
      <Divider />
      <div className="px-3 py-2">
        <div className="text-[10px] font-bold uppercase tracking-wider mb-2" style={{ color: 'var(--text-tertiary)' }}>
          {t('player.find_dac_title') || 'Find My DAC'}
        </div>
        <div className="flex gap-2 items-center">
          <input
            ref={inputRef}
            type="text"
            value={ip}
            onChange={e => { setIp(e.target.value); setStatus(''); }}
            onKeyDown={e => e.key === 'Enter' && handleProbe()}
            placeholder="192.168.1.x"
            disabled={probing}
            className="flex-1 rounded px-2.5 py-1.5 text-xs outline-none"
            style={{
              background: 'var(--surface-elevated)',
              color: 'var(--text-primary)',
              border: '1px solid var(--border)',
            }}
          />
          <button
            onClick={handleProbe}
            disabled={probing || !ip.trim()}
            className="rounded px-3 py-1.5 text-xs font-medium transition-opacity"
            style={{
              background: GOLD,
              color: '#000',
              opacity: probing || !ip.trim() ? 0.5 : 1,
            }}
          >
            {probing ? '...' : t('player.find_dac_search') || 'Search'}
          </button>
        </div>
        {status && (
          <div className="mt-1.5 text-[11px]" style={{ color: status.includes('Found') ? '#22c55e' : 'var(--text-tertiary)' }}>
            {status}
          </div>
        )}
        <div className="mt-1 text-[10px]" style={{ color: 'var(--text-tertiary)', opacity: 0.6 }}>
          {t('player.find_dac_hint') || "Find your DAC's IP in its display or your router's device list"}
        </div>
      </div>
    </>
  );
}
